/* eslint-disable react-hooks/exhaustive-deps */
import { Button, MenuItem, Stack, TextField, Typography } from '@ethings/ui-toolkit';
import { FormControl } from '@mui/material';
import { ChangeEvent, FC, useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { useBoolFlag } from '../../components';
import { usePromise, useResponsiveText } from '../../hooks';
import { getAvailableEventTypes, getEventLabelWithFlag, getFeatureLabelWithFlag, innerText, useAlertableFeatures } from '../../util';
import { FILTER_NON_ALERTABLE_DATA_FLAG } from '../subscription/feature-alert-settings-card';
import { FeatureAlertSettings } from './create-subscription.types';

interface Props {
  onActiveBack: () => void;
  onActiveNext: () => void;
  updateFeatureAlertSettings: (settings: FeatureAlertSettings) => void;
}
export const CreateSubscriptionFeatureAlertSettings: FC<Props> = ({ onActiveBack, onActiveNext, updateFeatureAlertSettings }): JSX.Element => {
  const { restClient } = useAuthenticated();
  const { t } = useTranslation(['admin', 'common']);
  const {
    alertableFeatures,
    alertableFeaturesError,
    alertableFeatureTypes,
    alertableFeatureTypesError,
    excludedFeatures,
  } = useAlertableFeatures();
  const [settings, setSettings] = useState<FeatureAlertSettings>({
    alertTypeId: null,
    feature: null,
    featureTypeIdPrefix: null,
  });
  const filterNonAlertableData = useBoolFlag(FILTER_NON_ALERTABLE_DATA_FLAG);
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');

  const titleText = t('admin:page.create-subscription.feature-alert-setting.labels');
  const h5Styles = useResponsiveText('1.5rem', innerText(titleText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  const [featureTypePrefixes, featureTypePrefixesError] = usePromise<string[]>(
    async () => {
      const result = await restClient.my.featureTypeIdPrefixes.getAll();
      const data = filterNonAlertableData
        ? result.filter((prefix) => alertableFeatureTypes?.some(({ _id }) => _id.startsWith(prefix)))
        : result;
      return data.sort();
    },
    [restClient, alertableFeatureTypes, filterNonAlertableData],
  );

  const [allFeatureInstances, allFeatureInstancesError] = usePromise<string[]>(
    async () => restClient.my.featureInstances.getAll(),
    [restClient],
  );

  const [features, featuresError] = usePromise<string[]>(
    async () => {
      const result = await restClient.my.featureInstances.getAll({
        featureTypeIdPrefix: settings.featureTypeIdPrefix ?? undefined,
      });
      const data = settings.featureTypeIdPrefix || !filterNonAlertableData
        ? result
        : result.filter((feature) => alertableFeatures?.some(({ _id }) => _id === feature));
      return data.sort();
    },
    [restClient, settings.featureTypeIdPrefix, alertableFeatures, filterNonAlertableData],
  );

  const availableFeatures = useMemo(() => {
    if (!features || !alertableFeatures || !alertableFeatureTypes) return;
    if (!filterNonAlertableData || !excludedFeatures) {
      return features;
    }

    return features.filter((featureInstance) => {
      const feature = featureInstance.split('/')[0];
      const featureData = alertableFeatures.find(({ _id }) => _id === feature);
      const featureType = alertableFeatureTypes.find(({ _id }) => _id === featureData?.featureTypeId);
      const eventTypes = getAvailableEventTypes(feature, featureType, excludedFeatures);
      return eventTypes.length > 0;
    });
  }, [features, alertableFeatures, alertableFeatureTypes, excludedFeatures, filterNonAlertableData]);

  const availableFeatureTypePrefixes = useMemo(() => {
    if (!featureTypePrefixes || !allFeatureInstances) return undefined;

    return featureTypePrefixes?.filter((featureTypePrefix) => {
      return allFeatureInstances?.find((featureInstance) => {
        const feature = featureInstance.split('/')[0];
        const featureData = alertableFeatures?.find(({ _id }) => _id === feature);
        const featureType = alertableFeatureTypes?.find(({ _id }) => _id === featureData?.featureTypeId);
        const eventTypes = getAvailableEventTypes(feature, featureType, excludedFeatures);
        return featureType?._id.startsWith(featureTypePrefix) && eventTypes.length > 0;
      });
    });
  }, [featureTypePrefixes, alertableFeatures, excludedFeatures, alertableFeatureTypes, allFeatureInstances]);

  const [alertTypes, alertTypesError] = usePromise<string[]>(
    async () => {
      const result = await restClient.my.alertTypes.getAll({
        featureTypeIdPrefix: settings.featureTypeIdPrefix ?? undefined,
      });
      return result.sort();
    },
    [restClient, settings.featureTypeIdPrefix],
  );

  const availableAlertTypes = useMemo(() => {
    if (!alertTypes || !alertableFeatures || !alertableFeatureTypes) return;
    if (!settings.feature || !filterNonAlertableData || !excludedFeatures) {
      return alertTypes;
    }

    return alertTypes.filter((alertType) => {
      const feature = settings.feature?.split('/')[0];
      const featureData = alertableFeatures.find(({ _id }) => _id === feature);
      const featureType = alertableFeatureTypes.find(({ _id }) => _id === featureData?.featureTypeId);
      const eventTypes = getAvailableEventTypes(feature ?? undefined, featureType, excludedFeatures);
      return eventTypes.includes(alertType);
    });
  }, [alertTypes, alertableFeatures, alertableFeatureTypes, filterNonAlertableData, excludedFeatures, settings.feature]);

  const handleFeatureChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSettings((settings) => {
      const feature = event.target.value.split('/')[0];
      const featureData = alertableFeatures?.find(({ _id }) => _id === feature);
      const featureType = alertableFeatureTypes?.find(({ _id }) => _id === featureData?.featureTypeId);
      const eventTypes = getAvailableEventTypes(feature, featureType, excludedFeatures);

      return {
        ...settings,
        feature: event.target.value,
        ...(settings.alertTypeId && !eventTypes.includes(settings.alertTypeId) && { alertTypeId: null }),
      };
    });
  };

  useEffect(() => updateFeatureAlertSettings(settings), [settings]);

  const nextDisabled = !settings.featureTypeIdPrefix || !settings.feature || !settings.alertTypeId;

  if (alertableFeatureTypesError || alertableFeaturesError || featureTypePrefixesError || featuresError || alertTypesError || allFeatureInstancesError) {
    return (
      <Stack spacing={2}>
        <Typography
          variant="h5"
          style={{
            fontSize: h5Styles.fontSize,
            lineHeight: h5Styles.lineHeight,
            wordBreak: h5Styles.wordBreak,
            whiteSpace: h5Styles.whiteSpace,
            overflow: h5Styles.overflow,
            textOverflow: h5Styles.textOverflow,
          }}
        >
          {titleText}
        </Typography>
        <Typography>{t('admin:page.create-subscription.feature-alert-setting.hint')}</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography
        variant="h5"
        style={{
          fontSize: h5Styles.fontSize,
          lineHeight: h5Styles.lineHeight,
          wordBreak: h5Styles.wordBreak,
          whiteSpace: h5Styles.whiteSpace,
          overflow: h5Styles.overflow,
          textOverflow: h5Styles.textOverflow,
        }}
      >
        {titleText}
      </Typography>
      <FormControl>
        <TextField
          label={t('admin:page.subscriptions-list.feature-type.label')}
          onChange={(e) => {
            setSettings({
              alertTypeId: null,
              feature: null,
              featureTypeIdPrefix: e.target.value,
            });
          }}
          select
          size="small"
          value={settings.featureTypeIdPrefix ?? ''}
          data-testid='dropdown-all-select'
          disabled={!availableFeatureTypePrefixes}
        >
          {availableFeatureTypePrefixes?.length
            ? availableFeatureTypePrefixes.map((prefix, i) => <MenuItem key={i} value={prefix} data-testid={`dropdown-menu-item-${i}`}>{getFeatureLabelWithFlag(prefix, flagUseEventV3)}</MenuItem>)
            : <MenuItem />
          }
        </TextField>
      </FormControl>
      <FormControl>
        <TextField
          disabled={!settings.featureTypeIdPrefix || !availableFeatures?.length}
          label={t('common:terms.feature')}
          onChange={handleFeatureChange}
          select
          size="small"
          value={settings.feature ?? ''}
          data-testid='dropdown-menu-select'
        >
          {availableFeatures?.map((feature, i) => (
            <MenuItem key={i} value={feature} data-testid={`dropdown-menu-item-${i}`}>
              {feature.includes('camera')
                ? t(`common:features.camera-type.${feature}`)
                : getFeatureLabelWithFlag(feature, flagUseEventV3)}
            </MenuItem>
          )) ?? <MenuItem />}
        </TextField>
      </FormControl>
      <FormControl>
        <TextField
          disabled={!settings.feature || !availableAlertTypes?.length}
          label={t('common:terms.alert-type')}
          onChange={(e) => setSettings({ ...settings, alertTypeId: e.target.value })}
          select
          size="small"
          value={settings.alertTypeId ?? ''}
          data-testid='dropdown-menu-select'
        >
          {availableAlertTypes?.map((alertType, i) => (
            <MenuItem key={i} value={alertType} data-testid={`dropdown-menu-item-${i}`}>
              {(settings.featureTypeIdPrefix && settings.feature) ? getEventLabelWithFlag(`${settings.featureTypeIdPrefix}-v0`, alertType, settings.feature, flagUseEventV3) : alertType}
            </MenuItem>
          )) ?? <MenuItem />}
        </TextField>
      </FormControl>

      <Stack direction="row" spacing={2} justifyContent="flex-end" my={1}>
        <Button
          onClick={onActiveBack}
          variant="outlined"
          data-testid="button-back"
        >
          {t('common:common.action.back')}
        </Button>
        <Button
          disabled={nextDisabled}
          onClick={onActiveNext}
          variant="contained"
          data-testid="button-next"
        >
          {t('common:common.action.next')}
        </Button>
      </Stack>
    </Stack>
  );
};
